/**
 * Glass popover pickers for the mini-app icon editor: a floating-panel shell
 * plus the emoji picker (Noto-rendered), the Lucide icon picker (searchable),
 * and the color picker. Each is a transient popover anchored to a trigger and
 * portaled to <body>, styled with the workspace glass kit (`.ws-menu`, DESIGN
 * DOC §10) so the frost samples the page behind it. See MiniappIconEditorDialog.
 */
import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Search } from 'lucide-react'
import { HexColorPicker } from 'react-colorful'
import { CURATED_LUCIDE, GlyphPreview } from './lucideGlyphGrid'

const HEX_RE = /^#[0-9a-fA-F]{6}$/



// ─── Floating glass panel ─────────────────────────────────────────────────────

interface FloatingPanelProps {
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
  width: number
  themeClass: string
  align?: 'left' | 'right'
  children: React.ReactNode
}

/**
 * A `.ws-menu` glass popover anchored under (or above, if it would overflow)
 * `anchorRef`, portaled to <body>. Closes on outside-click and Escape (the
 * latter captured + stopped so it dismisses only the popover, not the parent
 * dialog).
 */
function FloatingPanel({ anchorRef, onClose, width, themeClass, align = 'left', children }: FloatingPanelProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const a = anchorRef.current
    if (!a) return
    const r = a.getBoundingClientRect()
    let left = align === 'right' ? r.right - width : r.left
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    setPos({ top: r.bottom + 6, left })
  }, [anchorRef, width, align])

  // After the panel has a measured height, flip above the anchor if it would
  // overflow the viewport bottom.
  useLayoutEffect(() => {
    const el = ref.current
    const a = anchorRef.current
    if (!el || !a || !pos) return
    const h = el.offsetHeight
    if (pos.top + h <= window.innerHeight - 8) return
    const r = a.getBoundingClientRect()
    const above = r.top - 6 - h
    const next = above >= 8 ? above : Math.max(8, window.innerHeight - 8 - h)
    if (Math.abs(next - pos.top) > 1) setPos((p) => (p ? { ...p, top: next } : p))
  }, [pos, anchorRef])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!ref.current?.contains(t) && !anchorRef.current?.contains(t)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onClose, anchorRef])

  return createPortal(
    <div
      ref={ref}
      className={`${themeClass} ws-menu ws-anim-fade-in`}
      style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width, zIndex: 60 }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  )
}

// ─── Virtualized grid ─────────────────────────────────────────────────────────
//
// Windows a large icon/emoji grid so only the visible rows (plus a small
// overscan) mount. The emoji set is ~1900 cells; mounting them all — and having
// the browser rasterize that many emoji glyphs — stalled the popover on first
// open. Fixed cell height + column count keeps the row math trivial, no dep.

interface VirtualGridProps<T> {
  items: T[]
  cols: number
  cell: number
  viewH: number
  renderItem: (item: T, index: number) => React.ReactNode
  keyOf: (item: T, index: number) => string
}

function VirtualGrid<T>({ items, cols, cell, viewH, renderItem, keyOf }: VirtualGridProps<T>) {
  const ref = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  // A new result set (e.g. after a search keystroke) scrolls back to the top.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0
    setScrollTop(0)
  }, [items])

  const OVERSCAN = 3
  const rows = Math.ceil(items.length / cols)
  const totalH = rows * cell
  const startRow = Math.max(0, Math.floor(scrollTop / cell) - OVERSCAN)
  const endRow = Math.min(rows, Math.ceil((scrollTop + viewH) / cell) + OVERSCAN)
  const startIdx = startRow * cols
  const slice = items.slice(startIdx, endRow * cols)

  return (
    <div
      ref={ref}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      style={{ height: viewH, overflowY: 'auto', position: 'relative' }}
    >
      <div style={{ height: totalH, position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: startRow * cell,
            left: 0,
            right: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
          }}
        >
          {slice.map((it, i) => (
            <div key={keyOf(it, startIdx + i)} style={{ height: cell }}>
              {renderItem(it, startIdx + i)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Emoji picker (Noto-rendered) ─────────────────────────────────────────────

interface EmojiMartEmoji {
  id: string
  name: string
  keywords: string[]
  skins: { native: string }[]
}
interface EmojiMartData {
  categories: { id: string; emojis: string[] }[]
  emojis: Record<string, EmojiMartEmoji>
}

// Memoized so `@emoji-mart/data` (a large JSON) is imported + parsed ONCE per
// session. The dialog calls this on mount so the data is ready before the user
// ever opens the picker — the picker then renders in well under 100ms because
// there's no import/parse to wait on. Idempotent; concurrent callers share it.
let emojiDataPromise: Promise<EmojiMartData> | null = null
export function preloadEmojiData(): Promise<EmojiMartData> {
  if (!emojiDataPromise) {
    emojiDataPromise = import('@emoji-mart/data')
      .then((m) => ((m as { default?: unknown }).default ?? m) as EmojiMartData)
      .catch((e) => { emojiDataPromise = null; throw e })
  }
  return emojiDataPromise
}

// One representative emoji per category, for the quick-jump nav strip.
const CATEGORY_EMOJI: Record<string, string> = {
  people: '😀', nature: '🐻', foods: '🍔', activity: '⚽',
  places: '✈️', objects: '💡', symbols: '❤️', flags: '🏳️',
}

const EMOJI_COLS = 8
const EMOJI_CELL = 34
const EMOJI_HEADER_H = 26
const EMOJI_VIEW_H = 232

/** A virtual-list row: a category header, or a row of up to 8 emoji. */
type EmojiRow =
  | { kind: 'header'; label: string; catId: string }
  | { kind: 'emojis'; items: EmojiMartEmoji[] }

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n))
  return out
}

interface EmojiPickerPopoverProps {
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
  onSelect: (native: string) => void
  emojiStyle: 'color' | 'mono'
  themeClass: string
  searchPlaceholder: string
  noResults: string
  /** Category id → localized display label (e.g. people → "Smileys & People"). */
  categoryLabels: Record<string, string>
}

/**
 * Emoji picker: emojis grouped into category sections (with a quick-jump nav
 * strip), each glyph rendered in the selected Noto face (the Noto FontFace is
 * loaded by the dialog before this opens). Data comes from the memoized
 * `preloadEmojiData()` so first open is instant. Variable-height virtual list
 * (headers + emoji rows) keeps it fast regardless of the ~1900-emoji set.
 * Searchable by name / keyword (search collapses to a single flat section).
 */
export function EmojiPickerPopover({
  anchorRef,
  onClose,
  onSelect,
  emojiStyle,
  themeClass,
  searchPlaceholder,
  noResults,
  categoryLabels,
}: EmojiPickerPopoverProps) {
  const [data, setData] = useState<EmojiMartData | null>(null)
  const [query, setQuery] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)

  useEffect(() => {
    let alive = true
    preloadEmojiData().then((d) => { if (alive) setData(d) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const cellFont =
    emojiStyle === 'color'
      ? "'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI Emoji', system-ui, sans-serif"
      : "'Noto Emoji', system-ui, sans-serif"

  const q = query.trim().toLowerCase()

  // Build the flat row list (headers + emoji rows) and record each category's
  // header row index for the quick-jump nav.
  const { rows, catRowIndex } = useMemo(() => {
    const catRowIndex: Record<string, number> = {}
    if (!data) return { rows: [] as EmojiRow[], catRowIndex }
    if (q) {
      const matches: EmojiMartEmoji[] = []
      for (const c of data.categories) {
        for (const id of c.emojis) {
          const e = data.emojis[id]
          if (e && (e.id.includes(q) || e.name.toLowerCase().includes(q) || e.keywords.some((k) => k.includes(q)))) {
            matches.push(e)
          }
        }
      }
      return { rows: chunk(matches, EMOJI_COLS).map((items) => ({ kind: 'emojis', items }) as EmojiRow), catRowIndex }
    }
    const out: EmojiRow[] = []
    for (const c of data.categories) {
      const ems = c.emojis.map((id) => data.emojis[id]).filter(Boolean)
      if (!ems.length) continue
      catRowIndex[c.id] = out.length
      out.push({ kind: 'header', label: categoryLabels[c.id] ?? c.id, catId: c.id })
      for (const row of chunk(ems, EMOJI_COLS)) out.push({ kind: 'emojis', items: row })
    }
    return { rows: out, catRowIndex }
  }, [data, q, categoryLabels])

  // Cumulative offsets for the variable-height rows.
  const { offsets, totalH } = useMemo(() => {
    const offs: number[] = []
    let acc = 0
    for (const r of rows) { offs.push(acc); acc += r.kind === 'header' ? EMOJI_HEADER_H : EMOJI_CELL }
    return { offsets: offs, totalH: acc }
  }, [rows])

  // Reset scroll to top whenever the row set changes (e.g. a search keystroke).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    setScrollTop(0)
  }, [rows])

  const jumpTo = (catId: string) => {
    const i = catRowIndex[catId]
    if (i === undefined || !scrollRef.current) return
    scrollRef.current.scrollTop = offsets[i]
    setScrollTop(offsets[i])
  }

  // Visible row window (linear scan — a few hundred rows at most).
  const OVERSCAN = 6
  let start = 0
  while (start < rows.length - 1 && offsets[start + 1] <= scrollTop) start++
  start = Math.max(0, start - OVERSCAN)
  let end = start
  while (end < rows.length && offsets[end] < scrollTop + EMOJI_VIEW_H) end++
  end = Math.min(rows.length, end + OVERSCAN)
  const visible = rows.slice(start, end)

  return (
    <FloatingPanel anchorRef={anchorRef} onClose={onClose} width={300} themeClass={themeClass}>
      {/* `themed-text` is what the cells' `currentColor` resolves to — it flips
          with the theme, which is the whole point (see GlyphPreview). */}
      <div className="flex flex-col gap-1.5 p-1 themed-text" style={{ width: '100%' }}>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="themed-input w-full text-xs pl-7 pr-2 py-1.5 rounded"
            spellCheck={false}
          />
        </div>

        {/* Category quick-jump strip (hidden while searching). */}
        {data && !q && (
          <div className="flex items-center justify-between px-0.5">
            {data.categories.filter((c) => catRowIndex[c.id] !== undefined).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => jumpTo(c.id)}
                title={categoryLabels[c.id] ?? c.id}
                aria-label={categoryLabels[c.id] ?? c.id}
                className="w-6 h-6 flex items-center justify-center rounded icon-cell opacity-70 hover:opacity-100"
                style={{ fontFamily: cellFont, fontSize: 15, lineHeight: 1 }}
              >
                {CATEGORY_EMOJI[c.id] ?? '•'}
              </button>
            ))}
          </div>
        )}

        {!data ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={18} className="animate-spin themed-text-muted" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-xs themed-text-muted">{noResults}</div>
        ) : (
          <div
            ref={scrollRef}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            style={{ height: EMOJI_VIEW_H, overflowY: 'auto', position: 'relative' }}
          >
            <div style={{ height: totalH, position: 'relative' }}>
              {visible.map((r, k) => {
                const idx = start + k
                const top = offsets[idx]
                if (r.kind === 'header') {
                  return (
                    <div
                      key={`h${idx}`}
                      style={{ position: 'absolute', top, left: 0, right: 0, height: EMOJI_HEADER_H }}
                      className="flex items-end px-1 pb-1 text-[11px] font-medium themed-text-muted"
                    >
                      {r.label}
                    </div>
                  )
                }
                return (
                  <div
                    key={`r${idx}`}
                    style={{
                      position: 'absolute', top, left: 0, right: 0, height: EMOJI_CELL,
                      display: 'grid', gridTemplateColumns: `repeat(${EMOJI_COLS}, 1fr)`,
                    }}
                  >
                    {r.items.map((e) => {
                      const native = e.skins[0]?.native
                      if (!native) return null
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => onSelect(native)}
                          title={e.name}
                          aria-label={e.name}
                          className="w-full h-full flex items-center justify-center rounded icon-cell"
                          style={{ fontFamily: cellFont, fontSize: 20, lineHeight: 1 }}
                        >
                          {native}
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </FloatingPanel>
  )
}

// ─── Lucide icon picker (searchable) ──────────────────────────────────────────


interface IconPickerPopoverProps {
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
  onSelect: (name: string) => void
  selected?: string
  themeClass: string
  searchPlaceholder: string
  noResults: string
}

/**
 * Searchable Lucide icon picker. Shows a curated shortlist until the user types,
 * then filters the full Lucide name list. Renders a bounded number of results
 * (each via lazy `DynamicIcon`) so a search never fans out to thousands of
 * chunk loads.
 */
export function IconPickerPopover({
  anchorRef,
  onClose,
  onSelect,
  selected,
  themeClass,
  searchPlaceholder,
  noResults,
}: IconPickerPopoverProps) {
  const [query, setQuery] = useState('')
  const [allNames, setAllNames] = useState<string[] | null>(null)

  useEffect(() => {
    let alive = true
    import('lucide-react/dynamic')
      .then((m) => { if (alive) setAllNames(m.iconNames as string[]) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const MAX_RESULTS = 96
  const results = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, '-')
    if (!q) return CURATED_LUCIDE
    const source = allNames ?? CURATED_LUCIDE
    return source.filter((n) => n.includes(q)).slice(0, MAX_RESULTS)
  }, [query, allNames])

  return (
    <FloatingPanel anchorRef={anchorRef} onClose={onClose} width={300} themeClass={themeClass}>
      <div className="flex flex-col gap-2 p-1" style={{ width: '100%' }}>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="themed-input w-full text-xs pl-7 pr-2 py-1.5 rounded"
            spellCheck={false}
          />
        </div>
        {results.length === 0 ? (
          <div className="py-8 text-center text-xs themed-text-muted">{noResults}</div>
        ) : (
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-8">
                <Loader2 size={18} className="animate-spin themed-text-muted" />
              </div>
            }
          >
            <VirtualGrid
              items={results}
              cols={8}
              cell={34}
              viewH={244}
              keyOf={(name) => name}
              renderItem={(name) => (
                <button
                  type="button"
                  onClick={() => onSelect(name)}
                  title={name}
                  aria-label={name}
                  className={`w-full h-full flex items-center justify-center rounded icon-cell ${
                    selected === name ? 'icon-cell-selected' : ''
                  }`}
                >
                  <GlyphPreview name={name} size={20} />
                </button>
              )}
            />
          </Suspense>
        )}
      </div>
    </FloatingPanel>
  )
}

// ─── Color picker ─────────────────────────────────────────────────────────────

interface ColorField2Props {
  label?: string
  color: string
  onChange: (color: string) => void
  themeClass: string
}

/** Trim, add a leading `#`, lowercase — for comparing/validating typed hex. */
function normalizeHex(s: string): string {
  const t = s.trim().toLowerCase()
  return t.startsWith('#') ? t : `#${t}`
}

/**
 * Editable + validated hex field. Keeps its OWN text state so the user can type
 * freely (partial values like `#1a` no longer snap back — the previous version
 * was a controlled input that only propagated a complete `#rrggbb`, so every
 * intermediate keystroke was rejected and reverted, making it look read-only). A
 * valid `#rrggbb` (with or without the leading `#`) commits via `onChange`; a
 * half-typed / invalid value is held locally and snaps back to the last valid
 * color on blur.
 */
function HexInput({ color, onChange }: { color: string; onChange: (c: string) => void }) {
  const [text, setText] = useState(color)
  // Reflect an EXTERNAL color change (picker drag, preset, mode switch) without
  // clobbering an in-progress edit: keep the local text if it already normalizes
  // to the incoming color.
  useEffect(() => {
    setText((prev) => (normalizeHex(prev) === color.toLowerCase() ? prev : color))
  }, [color])
  const handle = (raw: string) => {
    setText(raw)
    const v = normalizeHex(raw)
    if (HEX_RE.test(v)) onChange(v)
  }
  return (
    <input
      type="text"
      value={text}
      onChange={(e) => handle(e.target.value)}
      onBlur={() => setText(color)}
      spellCheck={false}
      className="w-full text-[11px] px-1.5 py-1 rounded border themed-border themed-bg-secondary themed-text font-mono text-center"
      aria-label="Hex color"
    />
  )
}

/**
 * A color swatch that opens a glass popover with a `react-colorful` picker and an
 * editable hex input — replacing the old in-modal `absolute` popover (which the
 * modal's `overflow-y-auto` clipped) with a portaled, glass-styled floating panel.
 */
export function ColorField({ label, color, onChange, themeClass }: ColorField2Props) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  return (
    <div className="relative inline-flex flex-col">
      {label && <div className="text-[11px] themed-text-muted mb-1">{label}</div>}
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-lg border themed-border cursor-pointer shrink-0"
        style={{ background: color }}
        aria-label={label ?? color}
      />
      {open && (
        <FloatingPanel anchorRef={anchorRef} onClose={() => setOpen(false)} width={184} themeClass={themeClass}>
          <div className="flex flex-col gap-2 p-1">
            <HexColorPicker color={color} onChange={onChange} style={{ width: 168, height: 128 }} />
            <HexInput color={color} onChange={onChange} />
          </div>
        </FloatingPanel>
      )}
    </div>
  )
}
